import { useEffect, useMemo, useState } from 'react';
import { getActiveExercises } from '../../domain/scoring/exerciseCatalog';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../domain/scoring/categories';
import { scoreWorkout } from '../../domain/scoring/scoreWorkout';
import { seedWeightForExercise } from '../../domain/scoring/whatIf';
import type { Category, ScoringSex } from '../../domain/scoring/types';
import type { LastEntries } from '../../components/LiftModal/LiftModal';
import type { LiftHighlightMap } from '../../domain/workouts/liftHighlights';
import { LiftBests } from '../../components/LiftBests/LiftBests';
import './WhatIfModal.css';

export interface WhatIfModalProps {
  onClose: () => void;
  /** Defaults for the editable body weight / scoring sex fields. */
  bodyWeightLb: number;
  scoringSex: ScoringSex;
  /** The last weight logged per exercise; seeds the weight field. */
  lastEntries?: LastEntries;
  /** Current month's best points per category, to mark which rows beat them. */
  categoryBestPoints?: Partial<Record<Category, number>>;
  /** Each exercise's PR and month best, shown between the inputs and the table. */
  highlights?: LiftHighlightMap;
}

const REPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Guards scoreWorkout, which throws on a non-positive or non-integer weight. */
function isScorableWeight(weight: number, isBodyWeightLift: boolean): boolean {
  if (!Number.isInteger(weight)) return false;
  return isBodyWeightLift ? weight >= 0 : weight >= 1;
}

export function WhatIfModal({
  onClose,
  bodyWeightLb,
  scoringSex,
  lastEntries,
  categoryBestPoints,
  highlights,
}: WhatIfModalProps) {
  const exercises = useMemo(() => getActiveExercises(), []);

  const [exerciseId, setExerciseId] = useState(exercises[0]?.id ?? '');
  // Seeded from the profile but never written back - this is a scratch pad.
  const [bodyWeight, setBodyWeight] = useState(String(bodyWeightLb));
  const [sex, setSex] = useState<ScoringSex>(scoringSex);
  /**
   * What the user typed, or null while the field is still showing the seed.
   * Derived-not-stored, so the seed can arrive late (history is async) or change
   * with the exercise without an effect racing the user's typing.
   */
  const [typedWeight, setTypedWeight] = useState<string | null>(null);

  const exercise = exercises.find((ex) => ex.id === exerciseId);
  const isBodyWeightLift = (exercise?.bodyWeightFactor ?? 0) > 0;
  const isPerDumbbell = (exercise?.inputWeightMultiplier ?? 1) > 1;

  // Body weight drives the seed and the score, so fall back to the profile value
  // while the field is mid-edit rather than scoring against nothing.
  const parsedBodyWeight = parseInt(bodyWeight, 10);
  const effectiveBodyWeight =
    Number.isFinite(parsedBodyWeight) && parsedBodyWeight >= 50 && parsedBodyWeight <= 700
      ? parsedBodyWeight
      : bodyWeightLb;

  // The weight to start from: the last one logged for this lift, else what a
  // 100-point single would take, else nothing added for a bodyweight lift.
  const seedWeight = useMemo(() => {
    if (!exercise) return 0;
    const last = lastEntries?.[exerciseId];
    if (last !== undefined) return Math.round(last.weightLb);
    if (isBodyWeightLift) return 0;
    return seedWeightForExercise(effectiveBodyWeight, sex, exercise);
  }, [exercise, exerciseId, lastEntries, isBodyWeightLift, effectiveBodyWeight, sex]);

  const weight = typedWeight ?? String(seedWeight);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const parsedWeight = parseInt(weight, 10);
  const enteredWeight = Number.isFinite(parsedWeight) ? parsedWeight : NaN;
  const canScore = exercise !== undefined && isScorableWeight(enteredWeight, isBodyWeightLift);

  const rows = useMemo(() => {
    if (!exercise || !canScore) return [];
    return REPS.map((reps) => {
      const result = scoreWorkout({
        enteredWeightLb: enteredWeight,
        reps,
        inputWeightMultiplier: exercise.inputWeightMultiplier,
        normalizationMultiplier: exercise.normalizationMultiplier,
        bodyWeightFactor: exercise.bodyWeightFactor,
        bodyWeightLb: effectiveBodyWeight,
        scoringSex: sex,
        category: exercise.category,
      });
      return {
        reps,
        estimated1Rm: Math.round(result.estimated1RmLb),
        points: Math.round(result.points),
      };
    });
  }, [exercise, canScore, enteredWeight, effectiveBodyWeight, sex]);

  const categoryBest = exercise ? categoryBestPoints?.[exercise.category] : undefined;
  const beatsBest = (points: number) => categoryBest !== undefined && points > categoryBest;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="whatif__overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatif-title"
    >
      <div className="whatif__container">
        <div className="whatif__handle" />
        <button className="whatif__close" onClick={onClose} aria-label="Close" type="button">
          &times;
        </button>

        <h2 id="whatif-title" className="whatif__title">
          What If
        </h2>
        <p className="whatif__subtitle">Nothing here is saved or affects your profile</p>

        <div className="whatif__fields">
          <div className="whatif__field whatif__field--select">
            <label htmlFor="whatif-exercise">Exercise</label>
            <select
              id="whatif-exercise"
              value={exerciseId}
              onChange={(e) => {
                setExerciseId(e.target.value);
                // Back to showing the new lift's seed rather than carrying over
                // a weight that means something different for another exercise.
                setTypedWeight(null);
              }}
            >
              {/* Grouped by category rather than prefixing each name, matching
                  the leaderboard and history pickers. */}
              {CATEGORY_ORDER.map((category) => (
                <optgroup key={category} label={CATEGORY_LABELS[category]}>
                  {exercises
                    .filter((ex) => ex.category === category)
                    .map((ex) => (
                      <option key={ex.id} value={ex.id}>
                        {ex.displayName}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="whatif__row">
            <div className="whatif__field">
              <label htmlFor="whatif-bodyweight">Body weight (lbs)</label>
              <input
                id="whatif-bodyweight"
                type="number"
                inputMode="numeric"
                value={bodyWeight}
                onChange={(e) => setBodyWeight(e.target.value)}
                min="50"
                max="700"
                step="1"
              />
            </div>

            <div className="whatif__field">
              <label htmlFor="whatif-sex">Scoring sex</label>
              <button
                id="whatif-sex"
                type="button"
                className="whatif__toggle"
                onClick={() => setSex((s) => (s === 'male' ? 'female' : 'male'))}
                aria-label={`Scoring sex: ${sex === 'male' ? 'Male' : 'Female'}. Tap to change.`}
              >
                {sex === 'male' ? 'Male' : 'Female'}
              </button>
            </div>
          </div>

          <div className="whatif__field">
            {/* The hint rides in the label rather than under the input: on its own
                line it cost enough height to push row 10 of the table out of view. */}
            <label htmlFor="whatif-weight">
              {isBodyWeightLift ? 'Added weight (lbs)' : 'Weight (lbs)'}
              {isPerDumbbell && <span className="whatif__hint">per dumbbell</span>}
              {isBodyWeightLift && <span className="whatif__hint">0 for bodyweight</span>}
            </label>
            <input
              id="whatif-weight"
              type="number"
              inputMode="numeric"
              value={weight}
              onChange={(e) => setTypedWeight(e.target.value)}
              min={isBodyWeightLift ? '0' : '1'}
              max="2000"
              step="1"
            />
          </div>
        </div>

        {/* What this lift has actually done, between what you are asking and
            what the table answers. Historical, so the editable body weight and
            sex above do not touch it. */}
        <LiftBests highlights={highlights?.[exerciseId]} />

        <div className="whatif__table-wrap">
          {!canScore ? (
            <p className="whatif__empty">
              Enter a whole {isBodyWeightLift ? 'added weight' : 'weight'} to see the numbers.
            </p>
          ) : (
            <table className="whatif__table">
              <thead>
                <tr>
                  <th scope="col">Reps</th>
                  <th scope="col">Est. 1RM</th>
                  <th scope="col">Points</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.reps}
                    className={beatsBest(row.points) ? 'whatif__tr--beats' : undefined}
                  >
                    <td>{row.reps}</td>
                    <td>{row.estimated1Rm} lb</td>
                    <td className="whatif__points">
                      {row.points}
                      {beatsBest(row.points) && (
                        <span className="whatif__pr" aria-label="beats your current best">
                          ★
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {canScore && categoryBest !== undefined && (
          <p className="whatif__legend">
            ★ beats your {exercise ? CATEGORY_LABELS[exercise.category] : ''} best of{' '}
            {Math.round(categoryBest)} pts this month
          </p>
        )}
      </div>
    </div>
  );
}
